// @flow
import React, { useCallback, useMemo, useContext } from "react";
import Modal from "components/Modal";
import TopRowExtras from "./TopRowExtras";
import CloseButton from "@hs/close-button";
import FullscreenButton from "components/ModalFullscreenButton";
import styles from "./DocModal.module.css";
import { DocumentWithoutHash } from "views/document";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import bowser from "data/bowser";
import { createModal } from "react-modal-promise";
import {
    useEffectOnce,
    useModalPostMessageHandler,
    useArrowNavigationReceiver,
} from "hooks";
import { postMessageTo } from "hooks/PostMessage/Utils";
import { ArrowNavigationAction } from "hooks/PostMessage/Actions";
import { withModalCloseHandler } from "containers/ModalCloseHandler";
import { compose } from "redux";
import { ModalCloseContext } from "data/context";

export type DocModalProps = $Shape<{
    /** which rowIndex is currently displayed (for back&fwd buttons) */
    activeRowIndex: number,
    /** how many document exist (for back&fwd buttons) - set to -1 to disable */
    totalCount: number,
    /** whether to trigger Print */
    triggerPrint: boolean,
    /** whether to trigger EditMode */
    triggerEditMode: boolean,
    /** (optional) callback to reopen DocModal with same context but different itemUri
     * @param {string} itemUri itemUri to use to reopen DocModal
     */
    onReopenWith?: (itemUri: string) => void,
}>;

type Props = DocModalProps & {
    /** document's itemUri to show */
    itemUri: string,
    /** whether to show modal */
    open: boolean,
    /** callback when user closes modal */
    close: () => void,
    /** selected toggles */
    toggles?: Array<string>,
    /**pdf.js: search terms */
    searchTerm?: string,
    /** pdf.js: whether trigger browser print */
    triggerPrint: boolean,
    /** whether trigger form classification edit mode */
    triggerEditMode: boolean,
    /** whether to render arrow navigation */
    withArrowNavigation: boolean,
    /** whether to autosave on pagination */
    autoSave: boolean,
};

/**
 * DocViewer in Modal window
 */
export const DocModal = ({
    itemUri,
    activeRowIndex = 0,
    close,
    open,
    toggles,
    onReopenWith,
    searchTerm,
    triggerPrint,
    triggerEditMode,
    withArrowNavigation = true,
    autoSave = false,
}: Props) => {
    useEffectOnce(() => {
        postMessageTo(ArrowNavigationAction.Ready, true, window);
    });

    const fullScreenHandle = useFullScreenHandle();
    const { closeHandler } = useContext(ModalCloseContext);

    useModalPostMessageHandler(close);

    const handleDeletedWhileOpen = useCallback(
        ({
            itemUri,
            rowIndex,
            isNext,
            onSetActiveRowIndex,
            getActiveRowItemUri,
            minCount,
        }) => {
            //Go back until you find a valid itemUri
            while (itemUri == null && rowIndex > minCount) {
                rowIndex = onSetActiveRowIndex(--rowIndex, isNext);
                itemUri = getActiveRowItemUri(rowIndex);
            }
            if (itemUri != null && typeof onReopenWith === "function") {
                onReopenWith(itemUri);
            }
            return close();
        },
        [onReopenWith, close]
    );

    const handleClose = useCallback(async () => {
        // If no close handler is provided, we can close the modal
        if (typeof closeHandler !== "function") {
            close();
            return;
        }

        const canClose = await closeHandler?.();

        if (canClose) {
            close();
        }
    }, [close, closeHandler]);

    const {
        hasMoreThanOne,
        activeItemUri,
        onNext,
        onPrev,
        isNextDisabled,
        isPrevDisabled,
        paginationEvent,
    } = useArrowNavigationReceiver({ activeRowIndex, handleDeletedWhileOpen });

    const toggleFullscreen = useCallback(() => {
        /* istanbul ignore next */ !fullScreenHandle?.active
            ? fullScreenHandle.enter()
            : fullScreenHandle.exit();
    }, [fullScreenHandle]);

    const docItemUri = useMemo(
        () =>
            withArrowNavigation
                ? activeItemUri || itemUri
                : itemUri || activeItemUri,
        [activeItemUri, itemUri, withArrowNavigation]
    );

    return (
        <Modal
            enforceFocus={false}
            fullscreen
            headerSize={0}
            show={open !== false}
            onHide={close}
            style={{
                zIndex: 1050,
            }} /* correctly overlays email modal */
            dataTest="docModal"
        >
            <Modal.Body>
                <FullScreen
                    className={styles.fullscreen}
                    handle={fullScreenHandle}
                >
                    <DocumentWithoutHash
                        asModal={true}
                        itemUri={docItemUri}
                        searchTerm={searchTerm}
                        allowTogglesFromQueryString={false}
                        toggles={toggles}
                        triggerEditMode={triggerEditMode}
                        triggerPrint={triggerPrint}
                        paginationEvent={paginationEvent}
                        autoSave={autoSave}
                        topRowBefore={
                            hasMoreThanOne &&
                            withArrowNavigation && (
                                <TopRowExtras
                                    onPrev={onPrev}
                                    prevDisabled={isPrevDisabled}
                                    onNext={onNext}
                                    nextDisabled={isNextDisabled}
                                />
                            )
                        }
                        onPrev={
                            withArrowNavigation
                                ? onPrev
                                : /* istanbul ignore next */ () => {}
                        }
                        onNext={
                            withArrowNavigation
                                ? onNext
                                : /* istanbul ignore next */ () => {}
                        }
                        topRowEnd={
                            <>
                                {!bowser.is("iOS") && (
                                    <FullscreenButton
                                        isFullscreen={fullScreenHandle?.active}
                                        onClick={toggleFullscreen}
                                    />
                                )}
                                <CloseButton onClick={handleClose} />
                                {/* <CloseButton onClick={close} /> */}
                            </>
                        }
                    />
                </FullScreen>
            </Modal.Body>
        </Modal>
    );
};

const DocModalWithCloseHandler = compose(withModalCloseHandler)(DocModal);

export const openDocModal = createModal(DocModalWithCloseHandler);
